import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { userApi } from '../utils/api';
import toast from 'react-hot-toast';
import { useLanguageStore } from '../stores/languageStore';

const ForgotPassword: React.FC = () => {
  const { language } = useLanguageStore();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error(language === 'en' ? 'Please enter email address' : '请输入邮箱地址');
      return;
    }

    try {
      setLoading(true);
      const response = await userApi.forgotPassword(email);
      if (response.success) {
        setEmailSent(true);
        toast.success(language === 'en' ? 'Password reset email sent, please check your inbox' : '重置密码邮件已发送，请查收');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || (language === 'en' ? 'Send failed, please try again later' : '发送失败，请稍后再试'));
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="card p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{language === 'en' ? 'Email Sent' : '邮件已发送'}</h2>
          <p className="text-gray-600 mb-6">
            {language === 'en' ? `We have sent a password reset email to ${email}.` : `我们已向 ${email} 发送了一封密码重置邮件。`}
            {language === 'en' ? 'Please check your email and follow the instructions to reset your password.' : '请查收邮件并按照提示完成密码重置。'}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            {language === 'en' ? 'If you did not receive the email, please check your spam folder.' : '如果没有收到邮件，请检查垃圾邮件文件夹。'}
          </p>
          <Link to="/login" className="btn btn-primary">
            {language === 'en' ? 'Back to Login' : '返回登录'}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="card p-8 w-full max-w-md">
        <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">
          {language === 'en' ? 'Forgot Password' : '忘记密码'}
        </h1>
        <p className="text-center text-gray-600 mb-6">
          {language === 'en' ? 'Enter your registered email, we will send a password reset link' : '输入您的注册邮箱，我们会发送密码重置链接'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {language === 'en' ? 'Email Address' : '邮箱地址'}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder={language === 'en' ? 'Please enter registered email' : '请输入注册邮箱'}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary w-full"
          >
            {loading ? (language === 'en' ? 'Sending...' : '发送中...') : (language === 'en' ? 'Send Reset Link' : '发送重置链接')}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600">
          {language === 'en' ? 'Remember your password? ' : '想起密码了？'}
          <Link to="/login" className="link font-medium">
            {language === 'en' ? 'Back to Login' : '返回登录'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;